/* Enhancements for imported interior pages; form submission remains native Shopify. */
(() => {
  const initialize = (root = document) => {
    // Preserve captured accordion state while matching the source builder's
    // collapse distance after text wraps at a different viewport width.
    root.querySelectorAll('.sgAccordion:not(#sectionGroup67232931, #sectionGroup72576563)').forEach((group) => {
      if (group.dataset.seedCollapseSizing) return;
      group.dataset.seedCollapseSizing = 'true';
      const wrappers = [...group.querySelectorAll('.sectionCell.collapse > .secBody .secBodyWrapper')];
      const resize = () => wrappers.forEach((wrapper) => {
        wrapper.style.marginTop = `-${Math.floor(wrapper.getBoundingClientRect().height)}px`;
      });
      const observer = new ResizeObserver(resize);
      wrappers.forEach((wrapper) => observer.observe(wrapper));
      resize();
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(group)) observer.disconnect();
      }, { once: true });
    });
    root.querySelectorAll('[data-seed-pdf-parts]').forEach(async (reader) => {
      if (reader.dataset.seedBound) return;
      reader.dataset.seedBound = 'true';
      try {
        const parts = [];
        for (const url of reader.dataset.seedPdfParts.split(',')) {
          const response = await fetch(url);
          if (!response.ok) throw new Error('Catalog download failed');
          const binary = atob(await response.json());
          parts.push(Uint8Array.from(binary, (character) => character.charCodeAt(0)));
        }
        const url = URL.createObjectURL(new Blob(parts, { type: 'application/pdf' }));
        reader.querySelector('iframe').src = url;
        reader.querySelector('[data-seed-pdf-status]').hidden = true;
        window.addEventListener('pagehide', () => URL.revokeObjectURL(url), { once: true });
      } catch {
        reader.querySelector('[data-seed-pdf-status]').textContent = reader.dataset.seedPdfError;
      }
    });
    // Both source FAQs keep one answer open and close the previous answer.
    root.querySelectorAll('#sectionGroup67232931.sgAccordion, #sectionGroup72576563.sgAccordion').forEach((group) => {
      if (group.dataset.seedAccordion) return;
      group.dataset.seedAccordion = 'true';
      group.classList.add('sk-sg-initial-render');
      const cells = [...group.querySelectorAll('.sectionCell')];
      const update = (selected) => {
        cells.forEach((cell) => {
          const expanded = cell === selected;
          const body = cell.querySelector('.secBody');
          const wrapper = cell.querySelector('.secBodyWrapper');
          const trigger = cell.querySelector('.secCptn');
          if (!body || !wrapper || !trigger) return;
          cell.classList.toggle('active', expanded);
          cell.classList.toggle('noactive', !expanded);
          cell.classList.toggle('expand', expanded);
          cell.classList.toggle('collapse', !expanded);
          cell.setAttribute('rel', expanded ? 'expand' : 'collapse');
          body.classList.toggle('active', expanded);
          body.classList.toggle('noactive', !expanded);
          body.inert = !expanded;
          trigger.setAttribute('aria-expanded', String(expanded));
          const icon = cell.querySelector('.secIconColCell');
          icon?.classList.toggle('secIconExp', expanded);
          icon?.classList.toggle('secIconCol', !expanded);
          // Captured "completed" classes exposed answers outside the moving panel.
          body.classList.remove('sk-animation-completed');
          wrapper.style.marginTop = expanded ? '0px' : `-${Math.ceil(wrapper.getBoundingClientRect().height)}px`;
        });
      };
      cells.forEach((cell) => {
        const trigger = cell.querySelector('.secCptn');
        const body = cell.querySelector('.secBody');
        if (!trigger || !body) return;
        body.id ||= `${cell.id}-answer`;
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('aria-controls', body.id);
        cell.querySelector('.secHead').addEventListener('click', (event) => {
          event.preventDefault();
          update(cell);
        });
        trigger.addEventListener('keydown', (event) => {
          if (event.key === ' ') {
            event.preventDefault();
            trigger.click();
          }
        });
      });
      update(cells.find((cell) => cell.classList.contains('active')) || cells[0]);
      let frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => group.classList.remove('sk-sg-initial-render'));
      });
      // Keep the original collapse distance correct when fonts or wrapping change.
      const observer = new ResizeObserver(() => update(cells.find((cell) => cell.classList.contains('active'))));
      cells.forEach((cell) => {
        const wrapper = cell.querySelector('.secBodyWrapper');
        if (wrapper) observer.observe(wrapper);
      });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(group)) {
          observer.disconnect();
          cancelAnimationFrame(frame);
        }
      }, { once: true });
    });
    // Restore the continuous photo strip and arrows on Twilley's About Us page.
    root.querySelectorAll('#e59919510 .ig-type-slider').forEach((gallery) => {
      if (gallery.dataset.seedCarousel) return;
      const track = gallery.querySelector('.ig-mask');
      const slides = [...track.querySelectorAll(':scope > .ig-transition-item')];
      if (slides.length < 2) return;
      gallery.dataset.seedCarousel = 'true';
      const abort = new AbortController();
      const options = { signal: abort.signal };
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      track.querySelector('.ig-transition-slider-mgr')?.remove();
      const clone = (slide) => {
        const copy = slide.cloneNode(true);
        copy.inert = true;
        copy.setAttribute('aria-hidden', 'true');
        copy.removeAttribute('tabindex');
        return copy;
      };
      track.prepend(...slides.map(clone));
      track.append(...slides.map(clone));
      let position = 0;
      let width = 200;
      let span = width * slides.length;
      let target = null;
      let lastTime = 0;
      let frame;
      let pointerStart;
      let hovering = false;
      const draw = () => {
        track.style.transform = `translateX(${-span - position}px)`;
      };
      const resize = () => {
        const index = position / width;
        width = Math.min(200, gallery.clientWidth);
        span = width * slides.length;
        track.style.setProperty('--seed-slide-width', `${width}px`);
        track.style.width = `${span * 3}px`;
        position = index * width;
        target = null;
        draw();
      };
      const tick = (time) => {
        const elapsed = lastTime ? Math.min(time - lastTime, 50) : 0;
        lastTime = time;
        if (target !== null) {
          const distance = target - position;
          position += distance * Math.min(1, elapsed / 65);
          if (Math.abs(distance) < 0.5 || reducedMotion.matches) {
            position = (target + span) % span;
            target = null;
          }
        } else if (!reducedMotion.matches && !hovering && !gallery.matches(':focus-within') && !document.hidden) {
          position = (position + elapsed * 0.04) % span;
        }
        draw();
        frame = requestAnimationFrame(tick);
      };
      const move = (direction) => {
        const current = target ?? position;
        target = direction > 0
          ? (Math.floor(current / width + 0.001) + 1) * width
          : (Math.ceil(current / width - 0.001) - 1) * width;
        // Keep rapid repeated clicks within the cloned strip.
        if (target >= span * 2 || target < -span) {
          const shift = target >= span * 2 ? -span : span;
          target += shift;
          position += shift;
        }
      };
      for (const [selector, direction] of [['.ig-prev', -1], ['.ig-next', 1]]) {
        const button = gallery.querySelector(selector);
        button?.addEventListener('click', () => move(direction), options);
        button?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            move(direction);
          }
        }, options);
      }
      gallery.addEventListener('pointerenter', () => { hovering = true; }, options);
      gallery.addEventListener('pointerleave', () => { hovering = false; }, options);
      gallery.addEventListener('pointerdown', (event) => { pointerStart = event.clientX; }, options);
      gallery.addEventListener('pointerup', (event) => {
        if (pointerStart !== undefined && Math.abs(event.clientX - pointerStart) > 30) {
          move(event.clientX < pointerStart ? 1 : -1);
        }
        pointerStart = undefined;
      }, options);
      gallery.addEventListener('pointercancel', () => { pointerStart = undefined; }, options);
      const observer = new ResizeObserver(resize);
      observer.observe(gallery);
      resize();
      frame = requestAnimationFrame(tick);
      document.addEventListener('shopify:section:unload', (event) => {
        if (!event.target.contains(gallery)) return;
        cancelAnimationFrame(frame);
        observer.disconnect();
        abort.abort();
      }, { once: true });
    });
    root.querySelectorAll('.seed-source-form').forEach((form) => {
      if (form.dataset.seedBound) return;
      form.dataset.seedBound = 'true';
      // The legacy customer survey has no email field. Ask for the Shopify-required
      // reply address only when sending, preserving the original page layout.
      if (!form.querySelector('[name="contact[email]"]')) {
        form.addEventListener('submit', (event) => {
          if (form.querySelector('[name="contact[email]"]')) return;
          event.preventDefault();
          const dialog = document.querySelector('[data-seed-email-dialog]');
          if (!dialog) return;
          dialog.showModal();
          const emailForm = dialog.querySelector('form');
          const complete = (replyEvent) => {
            replyEvent.preventDefault();
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'contact[email]';
            input.value = emailForm.elements.email.value;
            form.append(input);
            dialog.close();
            form.requestSubmit();
          };
          const cleanup = () => emailForm.removeEventListener('submit', complete);
          emailForm.addEventListener('submit', complete);
          dialog.addEventListener('close', cleanup, { once: true });
        });
      }
    });
  };
  document.querySelector('[data-seed-email-cancel]')?.addEventListener('click', () => {
    document.querySelector('[data-seed-email-dialog]')?.close();
  });
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
